import { Users, ShieldCheck } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import SemanticInterlinker from '../components/SemanticInterlinker';
import './About.css';

export default function About() {
  const coreTeam = [
    {
      name: "Mr. Ashutosh Garg",
      title: "Managing Director",
      image: "/images/about/Mr. Ashutosh Garg.webp",
      description: "As the founder of DPS Educational Society in 2001, Mr. Ashutosh Garg is a true pioneer of IT education in Mathura. Guided by his resilience and visionary leadership, he has empowered over 150,000 students across the IT, retail, and fashion design sectors. Today, he continues to transform the region's educational landscape by conceptualizing and introducing the revolutionary idea of mobile-based digital examinations."
    },
    {
      name: "Er. Kalpana Garg",
      title: "Director Academics",
      image: "/images/about/Er. Kalpana Garg.webp",
      description: "Bringing unparalleled academic rigor to the institution, Er. Kalpana Garg holds an M.Tech in IT alongside four prestigious international certifications (Sun Java, MCSE, MCSD, and CCNA). As a pioneering IT educator in Mathura who has taught over 1.5 lakh students, she leverages her vast academic experience to meticulously design examination papers and lead the Review Committee for computer exams and Olympiads."
    },
    {
      name: "Er. Devansh Garg",
      title: "Exam Controller",
      image: "/images/about/Er. Devansh Garg.webp",
      description: "Er. Devansh Garg after his graduation has been a dedicated part of our organisational legacy. He has been decorating the designation of Project Manager as well as Exam Controller for various Skill Development Projects across central and state government skilling initiatives like Pradhan Mantri -Kaushal Vikas Yojana, Deen Dayal Upadhyay -Grameen Kaushalya Yojana, Uttar Pradesh Skill Development Mission , National Skill Development Corporation and Vocational Training Programmes under New Educational Policy, 2020."
    },
    {
      name: "Er. Saransh Garg",
      title: "System Controller",
      image: "/images/about/Er. Saransh Garg.webp",
      description: "An alumnus of Delhi Technological University with a degree in Mathematics and Computing, Er. Saransh Garg has been an integral pillar of DPS Educational Society since 2015. Bringing deep expertise in software development, machine learning, and AI, he is the technical architect who translated the vision of mobile-based examinations into a seamless reality through core software, website and exam portal development, while also managing key operations for the Ministry of IT's NIELIT CCC examinations."
    }
  ];

  const advisoryCommittee = [
    {
      name: "Retd. IRS Gyan Prakash Garg",
      title: "Commerce & Maths Lead",
      image: "/images/about/Retd. IRS Gyan Prakash Garg.webp",
      description: "With a distinguished 38-year career in government administration, Retd. IRS Gyan Prakash Garg serves as a pillar of procedural integrity for DPS Educational Society. As a retired Assistant Commissioner of Central Excise and Customs, he brings unparalleled expertise in taxation, economics, commerce, and regulatory compliance. On the advisory panel, he ensures strict administrative discipline, procedural fairness, and assessment integrity, while also lending his deep subject matter expertise to the Review Committee for Commerce and Mathematics examinations."
    },
    {
      name: "Prof. Suman Lata Jain",
      title: "Social Sciences Lead",
      image: "/images/about/Prof. Suman Lata Jain.webp",
      description: "A highly respected academician, Prof. Suman Lata Jain has been instrumental in guiding the organization through her extensive background in Political Science. As the retired Principal and Head of the Political Science Department at NAS Degree College, Meerut, she brings decades of expertise in academic administration, examination conduct, and paper setting. She plays a crucial role on the Review Committee, ensuring the highest academic standards for Social Science examinations and Olympiads."
    },
    {
      name: "Prof. Renu Bala Garg",
      title: "Social Sciences & Hindi Co-Lead",
      image: "/images/about/Prof. Renu Bala Garg.webp",
      description: "Drawing upon a rich background in the humanities, Prof. Renu Bala Garg provides invaluable academic guidance to the society. The retired Principal and Head of the Humanities Department at IOP Vrindavan, she possesses immense experience in institutional leadership and academic assessment. She actively shapes the curriculum and quality standards as a key member of the Review Committee for Social Sciences and Hindi Olympiads."
    },
    {
      name: "Er. Pragya Diwedi",
      title: "English & Computers Lead",
      image: "/images/about/Er. Pragya Diwedi.webp",
      description: "Er. Pragya Dwivedi is a Networking and Cybersecurity Expert at Reliance Industries (Jio), Mumbai, specializing in information security for large-scale digital infrastructures. Her deep expertise in data protection, access control, and risk assessment ensures the absolute security and privacy of the digital examination platforms. She guides the implementation of robust anti-malpractice safeguards and serves on the Review Committee for English and Computer examinations."
    },
    {
      name: "Er. Priyansh Garg",
      title: "Science Lead",
      image: "/images/about/Er. Priyansh Garg.webp",
      description: "Based at a Norwegian engineering firm, Er. Priyansh Garg is a Senior Solution Developer with extensive experience architecting enterprise-level digital systems. He serves as the primary technical advisor for the examination platform, overseeing assessment workflows, secure test delivery, question bank management, and advanced result analytics. Additionally, he lends his analytical expertise to the Review Committee for Science examinations."
    },
    {
      name: "Mrs. Sapna Garg",
      title: "Hindi Lead",
      image: "/images/about/Mrs. Sapna Garg.webp",
      description: "Mrs. Sapna Garg brings a strong creative and organizational vision to the team, having led the design and architectural development of the organization's web presence. She has been a key driving force behind the Digital Marketing and Graphic Designing divisions of the organisation, while also contributing her expertise to the Review Committee for the Hindi Olympiad."
    }
  ];

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "mainEntity": {
      "@type": "Organization",
      "name": "Dynamic Professional Skills Educational Society",
      "alternateName": "DPS Educational Society",
      "url": "https://dynamicexams.org",
      "description": "A premier educational institution dedicated to elevating the academic landscape for students across Uttar Pradesh through global-standard Olympiads and examinations.",
      "founder": {
        "@type": "Person",
        "name": "Mr. Ashutosh Garg"
      }
    }
  };

  return (
    <div className="about-page">
      <Helmet>
        <title>About Us | Dynamic Exams & DPS Educational Society</title>
        <meta name="description" content="Learn about Dynamic Professional Skills (DPS) Educational Society. Meet our core team and advisory committee dedicated to revolutionizing mobile-based digital examinations." />
        <meta property="og:title" content="About Us | Dynamic Exams" />
        <meta property="og:description" content="Discover our mission, our visionary team, and our commitment to accessible, frictionless digital examinations." />
        <script type="application/ld+json">
          {JSON.stringify(aboutSchema)}
        </script>
      </Helmet>
      {/* Dynamic Hero Section */}
      <div className="about-hero">
        <div className="about-hero-glow"></div>
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">Who We Are</h1>
            <SemanticInterlinker>
            <p className="hero-lead">
              Dynamic Professional Skills (DPS) Educational Society, in association with Kedti Computer Institute, is a premier educational institution dedicated to elevating the academic landscape for students across Uttar Pradesh. Founded by a team of passionate educators, our mission is to bring global-standard Olympiads and examinations directly to your school's doorstep.
            </p>
            <div className="hero-divider"></div>
            <p className="hero-sub">
              We are committed to fostering a deeper, analytical understanding of core subjects like Mathematics, Science, English, Hindi, Social Studies, Commerce and Computer Science to empower the next generation of critical thinkers.
            </p>
            <p className="hero-sub">
              At the heart of our vision is a commitment to accessible, frictionless assessment through cutting-edge technology. We have revolutionized the examination experience with a fully mobile-based platform engineered to support seamless concurrent testing and deliver rich, statistical performance analytics. By utilizing a 100% paperless and wireless approach, we champion an environmentally sustainable model that completely bypasses the traditional infrastructure limits and power bottlenecks often associated with large-scale assessments. Our robust digital ecosystem ensures that high-quality, data-driven testing is secure, scalable, and effortlessly accessible to every student, anywhere.
            </p>
            </SemanticInterlinker>
          </div>
        </div>
      </div>

      {/* Core Team Section */}
      <section className="team-section section-padding">
        <div className="container">
          <div className="section-header text-center">
            <h2 className="section-title text-glow"><Users className="inline-icon" /> Core Team Members</h2>
            <p className="section-subtitle">
              The core team at Dynamic Professional Skills (DPS) Educational Society is a powerhouse of visionary leadership, academic excellence, and technological expertise. With decades of combined experience spanning IT education, government skilling initiatives, and cutting-edge software development, this dedicated group works synergistically to transform the educational landscape and bring revolutionary mobile-based examinations to students everywhere.
            </p>
          </div>

          <div className="team-grid core-team-grid">
            {coreTeam.map((member, idx) => (
              <div className="flip-card" key={idx}>
                <div className="flip-card-inner">
                  {/* Front Side: Photo and Name */}
                  <div className="flip-card-front">
                    <img src={member.image} alt={member.name} loading="lazy" width="400" height="400" />
                    <div className="flip-card-overlay">
                      <h3>{member.name}</h3>
                      <span className="team-role gold-text">{member.title}</span>
                    </div>
                  </div>
                  {/* Back Side: Details */}
                  <div className="flip-card-back">
                    <h3>{member.name}</h3>
                    <span className="team-role gold-text">{member.title}</span>
                    <div className="flip-card-divider"></div>
                    <p>{member.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advisory Committee Section */}
      <section className="advisory-section section-padding">
        <div className="container">
          <div className="section-header text-center">
            <h2 className="section-title text-glow"><ShieldCheck className="inline-icon" /> Advisory Committee</h2>
            <p className="section-subtitle">
              The Advisory Committee brings together decades of administrative, academic, and technological expertise to ensure the integrity, security, and educational excellence of the Olympiads.
            </p>
          </div>

          <div className="team-grid advisory-team-grid">
            {advisoryCommittee.map((member, idx) => (
              <div className="flip-card alt-style" key={idx}>
                <div className="flip-card-inner">
                  {/* Front Side: Photo and Name */}
                  <div className="flip-card-front">
                    <img src={member.image} alt={member.name} loading="lazy" width="400" height="400" />
                    <div className="flip-card-overlay">
                      <h3>{member.name}</h3>
                      <span className="team-role cyan-text">{member.title}</span>
                    </div>
                  </div>
                  {/* Back Side: Details */}
                  <div className="flip-card-back">
                    <h3>{member.name}</h3>
                    <span className="team-role cyan-text">{member.title}</span>
                    <div className="flip-card-divider"></div>
                    <p>{member.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
